import React, { useEffect } from "react";
import OpcChatView from "../OpcChatView/OpcChatView";
import okrPlanCoachChatStore, {
  COACH_CARD,
  coachCardKind,
  parseCardRow,
  useCoachChat,
} from "../../_socket/okrPlanCoachChatStore";
import OpcCardBubble from "../OpcCardBubble/OpcCardBubble";

// ═══════════════════════════════════════════════════════════════════════════
// A live coach chat (check-in / final review — BE 2.8, 2.9; CD23, CD33): the
// SME-look `OpcChatView` fed by `okrPlanCoachChatStore`. Opens the chat on
// mount (`get_chat_messages`), draws remark / summary-change cards, and puts
// the suggestions inside the input: the BE's quick-reply chips while they're
// live, otherwise the FE-only starters not yet asked.
//   `onCard(kind, card)` returns { onConfirm, onDecline } for a card.
// ═══════════════════════════════════════════════════════════════════════════

const OpcChatPanel = ({
  chatId,
  featureSlug,
  owner,
  readOnly,
  starters = [],
  onCard,
  footerNote,
}) => {
  const chat = useCoachChat(chatId);
  useEffect(() => {
    if (!chatId) return undefined;
    okrPlanCoachChatStore.getState().openChat(chatId, owner);
    return () => okrPlanCoachChatStore.getState().closeChat(chatId);
  }, [chatId]); // eslint-disable-line

  const send = (text) =>
    okrPlanCoachChatStore.getState().sendMessage(chatId, text, featureSlug);
  const last = chat.messages[chat.messages.length - 1];
  const asked = chat.messages
    .filter((m) => m.sender === "user")
    .map((m) => m.message);
  // BE chips win while they're live; otherwise the FE-only "Try:" starters.
  const chips =
    chat.chips?.options?.length && last?.sender !== "user"
      ? chat.chips.options
      : null;
  const tries = starters.filter((s) => !asked.includes(s));

  const renderCustomMessage = (message, index) => {
    const kind = coachCardKind(message);
    if (!kind) return null;
    const card = parseCardRow(message);
    const handlers = onCard ? onCard(kind, card) : {};
    return (
      <OpcCardBubble
        key={card.message_id || `card-${index}`}
        kind={kind === COACH_CARD.REMARK ? "remark" : "change"}
        card={card}
        readOnly={readOnly}
        busy={!!chat.cardBusy[card.message_id]}
        error={chat.cardErrors[card.message_id]}
        onConfirm={handlers.onConfirm}
        onDecline={handlers.onDecline}
      />
    );
  };

  return (
    <OpcChatView
      messages={chat.messages}
      loading={chat.loading}
      streamLoading={chat.streamLoading}
      streamMessage={chat.streamMessage}
      messageLoading={chat.messageLoading}
      readOnly={readOnly}
      error={chat.error}
      onSend={send}
      renderCustomMessage={renderCustomMessage}
      footerNote={footerNote}
      quickStarters={chips || tries}
    />
  );
};

export default OpcChatPanel;
